import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["xSparkline", "mrSparkline"];

  connect() {
    console.log("xmr controller connected");
    // Parse data from the HTML attribute
    const xValues = this.parseData(this.xSparklineTarget.dataset.xmrValues);

    if (!xValues || xValues.length === 0) {
      console.error("No data provided for X chart.");
      return;
    }

    // Compute MR values
    const mrValues = xValues.slice(1).map((v, i) => Math.abs(v - xValues[i]));

    // Compute limits
    const xMean = this.mean(xValues);
    const xStdDev = this.stdDev(xValues);
    const xUCL = xMean + 3 * xStdDev;
    const xLCL = xMean - 3 * xStdDev;

    const mrMean = this.mean(mrValues);
    const mrUCL = mrMean * 3.267;

    // Render sparklines
    this.renderSparkline(this.xSparklineTarget, xValues, xUCL, xLCL, xMean, "X Chart");
    this.renderSparkline(this.mrSparklineTarget, mrValues, mrUCL, 0, mrMean, "MR Chart");
  }

  parseData(data) {
    if (!data) return [];
    return data.split(",").map((value) => parseFloat(value));
  }

  renderSparkline(target, values, ucl, lcl, cl, label) {
    const width = 150;
    const height = 120;
    const padding = 0;

    // Scale values to fit in the SVG dimensions
    const minValue = Math.min(lcl, ...values);
    const maxValue = Math.max(ucl, ...values);
    const scaleY = (height - 2 * padding) / (maxValue - minValue);
    const scaleX = (width - 2 * padding) / (values.length - 1);

    const points = values.map((v, i) => [
      padding + i * scaleX,
      height - padding - (v - minValue) * scaleY,
    ]);

    // Create SVG
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", `0 0 ${width} ${height}`);
    svg.setAttribute("width", width);
    svg.setAttribute("height", height);

    // Add UCL, LCL, and CL lines
    this.addLine(svg, padding, height - padding - (ucl - minValue) * scaleY, width - padding, height - padding - (ucl - minValue) * scaleY, "ucl");
    this.addLine(svg, padding, height - padding - (lcl - minValue) * scaleY, width - padding, height - padding - (lcl - minValue) * scaleY, "lcl");
    this.addLine(svg, padding, height - padding - (cl - minValue) * scaleY, width - padding, height - padding - (cl - minValue) * scaleY, "cl");

    // Add data line
    const path = points.map(([x, y], i) => (i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`)).join(" ");
    const dataLine = document.createElementNS("http://www.w3.org/2000/svg", "path");
    dataLine.setAttribute("d", path);
    dataLine.setAttribute("class", "data-line");
    svg.appendChild(dataLine);

    // Add data points
    points.forEach(([x, y]) => {
      const circle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
      circle.setAttribute("cx", x);
      circle.setAttribute("cy", y);
      circle.setAttribute("r", 2);
      circle.setAttribute("class", "data-point");
      svg.appendChild(circle);
    });

    // Append to target
    target.innerHTML = "";
    target.appendChild(svg);
  }

  addLine(svg, x1, y1, x2, y2, className) {
    const line = document.createElementNS("http://www.w3.org/2000/svg", "line");
    line.setAttribute("x1", x1);
    line.setAttribute("y1", y1);
    line.setAttribute("x2", x2);
    line.setAttribute("y2", y2);
    line.setAttribute("class", className);
    svg.appendChild(line);
  }

  mean(values) {
    return values.reduce((sum, val) => sum + val, 0) / values.length;
  }

  stdDev(values) {
    const mean = this.mean(values);
    const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / (values.length - 1);
    return Math.sqrt(variance);
  }
}